import axios from 'axios'
import type { ApiResponse, PaginatedResponse } from '@/types'
import { getAccessToken, getRefreshToken } from './auth'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Set Content-Type for JSON requests (not for FormData)
    if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json'
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      // Token expired, try to refresh
      try {
        const refreshToken = getRefreshToken()
        if (refreshToken) {
          const response = await axios.post(`${import.meta.env.VITE_API_URL || '/api'}/auth/refresh`, {
            refreshToken
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data.data
          
          // Update tokens in auth service
          const { setTokens } = await import('./auth')
          setTokens(accessToken, newRefreshToken, 15 * 60) // 15 minutes
          
          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return api.request(originalRequest)
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        const { clearTokens } = await import('./auth')
        clearTokens()
        window.location.href = '/login'
      }
    }
    
    return Promise.reject(error)
  }
)

// Generic API methods
export const apiClient = {
  get: <T>(url: string, params?: any): Promise<ApiResponse<T>> =>
    api.get(url, { params }).then(res => res.data),
    
  post: <T>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.post(url, data).then(res => res.data),
    
  put: <T>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.put(url, data).then(res => res.data),
    
  delete: <T>(url: string): Promise<ApiResponse<T>> =>
    api.delete(url).then(res => res.data),
    
  getPaginated: <T>(url: string, params?: any): Promise<PaginatedResponse<T>> =>
    api.get(url, { params }).then(res => res.data)
}

export default api
